import { FunctionComponent, useEffect } from 'react'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import EmbeddedContent from '@components/EmbeddedContent'
import config from '@config'
import useVideoRefs from '@hooks/useVideoRefs'
import styled, { css } from 'styled-components'
import useIsInArticleBodyContent from '@hooks/useIsInArticleBodyContent'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'
import useIsInCueLiveContent from '@hooks/useIsInCueLiveContent'
import { printCSS } from '@utils/style'

const {
  socialembeds: { youtubeEmbedStaticPageUrl },
} = config

interface YoutubeVideoAPIProps {
  videoId?: string
  uuid: string
}

const StyledYoutubeContainer = styled.div<{
  shouldStretch: boolean
}>`
  ${({
    shouldStretch,
    theme: {
      measures: { stretchWidthOnMobile },
    },
  }) => css`
    ${printCSS(css`
      display: none;
    `)}

    ${shouldStretch && stretchWidthOnMobile()};
    position: relative;
    margin-top: 16px;
    margin-bottom: 16px;
  `}
`

const StyledYoutubeMainElementWrapper = styled.div`
  padding-top: 56.25%;
  width: 100%;
  position: relative;
  overflow: hidden;
`

const StyledYoutubeMainElement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const StyledEmbeddedContent = styled(EmbeddedContent)`
  height: 100% !important;
`

const Youtube: FunctionComponent<YoutubeVideoAPIProps> = ({
  videoId,
  uuid,
}) => {
  const { removeVideoPlayer } = useVideoRefs()
  const isInArticleBody = useIsInArticleBodyContent()
  const isInRecipe = useIsInRecipeContent()
  const isInCueLive = useIsInCueLiveContent()

  const shouldStretch = isInArticleBody && !isInRecipe && !isInCueLive

  useEffect(() => {
    return () => {
      removeVideoPlayer(uuid)
    }
  }, [removeVideoPlayer, uuid])

  return (
    <StyledYoutubeContainer shouldStretch={shouldStretch}>
      <StyledYoutubeMainElementWrapper>
        <StyledYoutubeMainElement>
          <StyledEmbeddedContent
            url={`${youtubeEmbedStaticPageUrl}https://www.youtube.com/embed/${videoId}&widgetId=${uuid}`}
          />
        </StyledYoutubeMainElement>
      </StyledYoutubeMainElementWrapper>
    </StyledYoutubeContainer>
  )
}

const widget = {
  kind: ['widget', 'video', 'youtube'],
  component: Youtube,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
